import { useContext, useCallback, useRef, useMemo } from 'react';
import { MainContext } from '../contexts/MainProvider';
import {
  getTrackingPlatform,
  getAppVersion,
  getOSVersion,
  getDeviceModel,
  MobileTrackingEvents,
} from '../helpers/platformTracking';

interface LoginEventData {
  user_id: number;
  user_name: string;
  user_role: string;
  company_id?: number;
  company_name?: string;
}

interface SessionEventData {
  user_id: number;
  user_name: string;
  user_role: string;
  company_id?: number;
  company_name?: string;
}

interface PageViewEventData {
  page_name: string;
  user_id: number;
  user_name: string;
  user_role: string;
  company_id?: number;
  company_name?: string;
  time_spent_seconds?: number;
  entry_timestamp?: string;
}

interface PromptEventData {
  user_id: number;
  user_name: string;
  user_role: string;
  company_id?: number;
  company_name?: string;
  prompt_text?: string;
}

interface PageTimer {
  page_name: string;
  entry_timestamp: number;
}

/**
 * Mobile-specific tracking hook for Amplitude events
 * Provides consistent platform detection and event tracking for mobile apps
 */
export const useMobileTracking = () => {
  const { tracker, loggedUser, company } = useContext(MainContext);
  const pageTimer = useRef<PageTimer | null>(null);

  // Get tracking platform - single source of truth
  const trackingPlatform = useMemo(() => {
    const platformInfo = getTrackingPlatform();
    return platformInfo.platform;
  }, []);

  /**
   * Track user login event
   * Called after successful authentication
   */
  const trackLogin = useCallback(
    (additionalData?: Partial<LoginEventData>) => {
      if (!tracker || !loggedUser) return;

      const appVersion = getAppVersion();
      const osVersion = getOSVersion();
      const deviceModel = getDeviceModel();

      tracker.track(MobileTrackingEvents.USER_LOGIN, {
        user_id: loggedUser.id,
        user_name: loggedUser.name,
        user_role: loggedUser.role,
        company_id: company?.id,
        company_name: company?.name,
        platform: trackingPlatform,
        timestamp: new Date().toISOString(),
        app_version: appVersion,
        ...(osVersion && { os_version: osVersion }),
        ...(deviceModel && { device_model: deviceModel }),
        ...additionalData,
      });
    },
    [tracker, loggedUser, company, trackingPlatform]
  );

  /**
   * Track app session event
   * Called when user opens the app with an existing valid session
   */
  const trackSession = useCallback(
    (additionalData?: Partial<SessionEventData>) => {
      if (!tracker || !loggedUser) return;

      const appVersion = getAppVersion();
      const osVersion = getOSVersion();
      const deviceModel = getDeviceModel();

      tracker.track(MobileTrackingEvents.APP_SESSION, {
        user_id: loggedUser.id,
        user_name: loggedUser.name,
        user_role: loggedUser.role,
        company_id: company?.id,
        company_name: company?.name,
        platform: trackingPlatform,
        timestamp: new Date().toISOString(),
        app_version: appVersion,
        ...(osVersion && { os_version: osVersion }),
        ...(deviceModel && { device_model: deviceModel }),
        ...additionalData,
      });
    },
    [tracker, loggedUser, company, trackingPlatform]
  );

  /**
   * Start tracking time on a page
   * Call this when user enters a page - does NOT send an event
   * The event will be sent when trackPageView is called (on exit)
   */
  const startPageTracking = useCallback((pageName: string) => {
    if (!pageName) return;

    // Store entry timestamp for later
    pageTimer.current = {
      page_name: pageName,
      entry_timestamp: Date.now(),
    };
  }, []);

  /**
   * Track page view with time spent (app_page_view)
   * Call this when user LEAVES a page - sends the event with time_spent_seconds
   * Can also be called with explicit page_name and time_spent_seconds for cross-page tracking
   */
  const trackPageView = useCallback(
    (additionalData?: Partial<PageViewEventData>) => {
      if (!tracker || !loggedUser) {
        // Always clear the timer, regardless of which path was taken
        pageTimer.current = null;
        return;
      }

      // Allow explicit data (for cross-page tracking via localStorage)
      // or use pageTimer data
      const hasExplicitData = additionalData?.page_name && additionalData?.time_spent_seconds;
      if (!hasExplicitData && !pageTimer.current) {
        // Always clear the timer, regardless of which path was taken
        pageTimer.current = null;
        return;
      }

      const appVersion = getAppVersion();
      const osVersion = getOSVersion();
      const deviceModel = getDeviceModel();

      let pageName: string;
      let entryTimestamp: string;
      let timeSpentSeconds: number;

      if (hasExplicitData) {
        // Use explicit data passed in
        pageName = additionalData.page_name!;
        timeSpentSeconds = additionalData.time_spent_seconds!;
        entryTimestamp = additionalData.entry_timestamp || new Date().toISOString();
      } else {
        // Use pageTimer data
        const exitTimestamp = Date.now();
        pageName = pageTimer.current!.page_name;
        entryTimestamp = new Date(pageTimer.current!.entry_timestamp).toISOString();
        timeSpentSeconds = Math.floor((exitTimestamp - pageTimer.current!.entry_timestamp) / 1000);
      }

      const eventData = {
        user_id: loggedUser.id,
        user_name: loggedUser.name,
        user_role: loggedUser.role,
        company_id: company?.id,
        company_name: company?.name,
        page_name: pageName,
        entry_timestamp: entryTimestamp,
        time_spent_seconds: timeSpentSeconds,
        platform: trackingPlatform,
        app_version: appVersion,
        ...(osVersion && { os_version: osVersion }),
        ...(deviceModel && { device_model: deviceModel }),
      };
      tracker.track(MobileTrackingEvents.APP_PAGE_VIEW, eventData);

      // Always clear the timer, regardless of which path was taken
      pageTimer.current = null;
    },
    [tracker, loggedUser, company, trackingPlatform]
  );

  /**
   * Track FactoryGPT prompt submission
   * Call this when user sends a message to FactoryGPT
   */
  const trackPrompt = useCallback(
    (promptText?: string, additionalData?: Partial<PromptEventData>) => {
      if (!tracker || !loggedUser) return;

      const appVersion = getAppVersion();
      const osVersion = getOSVersion();
      const deviceModel = getDeviceModel();

      tracker.track(MobileTrackingEvents.FACTORYGPT_PROMPT_SENT, {
        user_id: loggedUser.id,
        user_name: loggedUser.name,
        user_role: loggedUser.role,
        company_id: company?.id,
        company_name: company?.name,
        platform: trackingPlatform,
        timestamp: new Date().toISOString(),
        app_version: appVersion,
        ...(osVersion && { os_version: osVersion }),
        ...(deviceModel && { device_model: deviceModel }),
        ...(promptText && { prompt_text: promptText }),
        ...additionalData,
      });
    },
    [tracker, loggedUser, company, trackingPlatform]
  );

  /**
   * Get current platform info (for conditional logic)
   */
  const getPlatformInfo = useCallback(() => {
    const platformInfo = getTrackingPlatform();
    return {
      platform: trackingPlatform,
      isAppMobile: platformInfo.isAppMobile,
      isWeb: platformInfo.isWeb,
    };
  }, [trackingPlatform]);

  return {
    trackLogin,
    trackSession,
    startPageTracking,
    trackPageView,
    trackPrompt,
    getPlatformInfo,
  };
};
